import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useCart } from "./CartContext";
import Popup from "../components/Popup";
import tablehead from "../include/tablehead.json";
import StickyTableHeader from "../components/utility/StickyTableHeader";
import AddToCard from "../components/utility/AddToCard";
import QuantityControl from "../components/utility/QuantityControl";
import SingleCategory from "../components/utility/SingleCategory";
import config from "../config";
import Navbar from "../include/Navbar";
import { useCurrency } from "../components/utility/CurrencyContext";
import "./table.css";

const Table = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { categoryId } = useParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [counters, setCounters] = useState({});
  const [filters, setFilters] = useState({});
  const [open, setOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { addToCart } = useCart();

  const { currency, setCurrency, convertPrice, getCurrencySymbol } =
    useCurrency();

  const handleCurrencyChange = (event) => {
    setCurrency(event.target.value);
  };

  const openPopup = (product, quantity) => {
    setSelectedProduct(product);
    addToCart(product, quantity);
    setOpen(true);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(
          `${config.baseURL}/productsfront/${categoryId}`
        );
        if (!response.ok) throw new Error("Failed to fetch products");
        const data = await response.json();
        setProducts(data);
        setFilteredProducts(data);

        // Initialize counters for quantity control
        const initialCounters = {};
        data.forEach((product) => {
          initialCounters[product.id] = 1;
        });
        setCounters(initialCounters);
      } catch (error) {
        console.error(error);
        alert("An error occurred while fetching products.");
      } 
    };
    fetchProducts();
  }, [categoryId]);

  // Handle filter change
  const handleFilterChange = (column, value) => {
    setFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  // Apply filters dynamically
  useEffect(() => {
    let filteredData = products;

    Object.entries(filters).forEach(([column, value]) => {
      if (value !== "") {
        filteredData = filteredData.filter(
          (item) => String(item[column]) === String(value)
        );
      }
    });

    setFilteredProducts(filteredData);
  }, [filters, products]);

  const increment = (id) => {
    setCounters((prev) => ({
      ...prev,
      [id]: (prev[id] || 1) + 1,
    }));
  };

  const decrement = (id) => {
    setCounters((prev) => ({
      ...prev,
      [id]: prev[id] > 1 ? prev[id] - 1 : 1,
    }));
  };

  const resetFilters = () => {
    setFilters({});
    setFilteredProducts(products);

    // Reset dropdowns to "All" by setting empty values
    const dropdowns = document.querySelectorAll("select");
    dropdowns.forEach((dropdown) => {
      dropdown.value = ""; // Reset each dropdown to default ("All" option)
    });
  };

  const handleInputChange = (e, productId) => {
    const newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue > 0) {
      setCounters((prev) => ({
        ...prev,
        [productId]: newValue,
      }));
    }
  };

  const searchedProducts = filteredProducts.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Navbar />
      <Popup open={open} setOpen={setOpen} product={selectedProduct} />
      <div className="relative isolate px-6 pt-10 lg:px-8">
        <div className="mx-auto max-w-7xl py-5 flex">
          <div className="w-1/4 p-4 bg-gray-100 rounded-lg mt-9">
            <h2 className="text-lg font-semibold mb-3">Filters</h2>

            {/* Bore Filter */}
            <label className="block mb-2">Bore (B):</label>
            <select
              className="w-full p-2 border rounded"
              onChange={(e) => handleFilterChange("bore", e.target.value)}
            >
              <option value="">All</option>
              {[...new Set(products.map((p) => p.bore))].map((bore) => (
                <option key={bore} value={bore}>
                  {bore}
                </option>
              ))}
            </select>

            {/* Thickness Filter */}
            <label className="block mt-3 mb-2">Thickness (t):</label>
            <select
              className="w-full p-2 border rounded"
              onChange={(e) => handleFilterChange("thick", e.target.value)}
            >
              <option value="">All</option>
              {[...new Set(products.map((p) => p.thick))].map((thick) => (
                <option key={thick} value={thick}>
                  {thick}
                </option>
              ))}
            </select>

            {/* Thickness Tolerance Filter */}
            <label className="block mt-3 mb-2">Thickness Tolerance:</label>
            <select
              className="w-full p-2 border rounded"
              onChange={(e) =>
                handleFilterChange("thickness_tolerance", e.target.value)
              }
            >
              <option value="">All</option>
              {[...new Set(products.map((p) => p.thickness_tolerance))].map(
                (tolerance) => (
                  <option key={tolerance} value={tolerance}>
                    +{tolerance}
                  </option>
                )
              )}
            </select>

            {/* Free Diameter Filter */}
            <label className="block mt-3 mb-2">Free Diameter (D):</label>
            <select
              className="w-full p-2 border rounded"
              onChange={(e) =>
                handleFilterChange("free_diameter", e.target.value)
              }
            >
              <option value="">All</option>
              {[...new Set(products.map((p) => p.free_diameter))].map(
                (diameter) => (
                  <option key={diameter} value={diameter}>
                    {diameter}
                  </option>
                )
              )}
            </select>

            {/* Clearance on Bore Filter */}
            <label className="block mt-3 mb-2">Clearance on Bore (Cb):</label>
            <select
              className="w-full p-2 border rounded"
              onChange={(e) =>
                handleFilterChange("clearance_onbore", e.target.value)
              }
            >
              <option value="">All</option>
              {[...new Set(products.map((p) => p.clearance_onbore))].map(
                (clearance) => (
                  <option key={clearance} value={clearance}>
                    {clearance}
                  </option>
                )
              )}
            </select>

            {/* Beam Filter */}
            <label className="block mt-3 mb-2">Beam (b):</label>
            <select
              className="w-full p-2 border rounded"
              onChange={(e) => handleFilterChange("beam", e.target.value)}
            >
              <option value="">All</option>
              {[...new Set(products.map((p) => p.beam))].map((beam) => (
                <option key={beam} value={beam}>
                  {beam}
                </option>
              ))}
            </select>

            {/* Price Filter */}
            <label className="block mt-3 mb-2">Price 1-249:</label>
            <select
              className="w-full p-2 border rounded"
              onChange={(e) =>
                handleFilterChange("price_1_249", e.target.value)
              }
            >
              <option value="">All</option>
              {[...new Set(products.map((p) => p.price_1_249))].map((price) => (
                <option key={price} value={price}>
                  {getCurrencySymbol()} {convertPrice(price)}
                </option>
              ))}
            </select>

            {/* Price 250-999 Filter */}
            <label className="block mt-3 mb-2">Price 250-999:</label>
            <select
              className="w-full p-2 border rounded"
              onChange={(e) =>
                handleFilterChange("price_250_999", e.target.value)
              }
            >
              <option value="">All</option>
              {[...new Set(products.map((p) => p.price_250_999))].map(
                (price) => (
                  <option key={price} value={price}>
                    {getCurrencySymbol()} {convertPrice(price)}
                  </option>
                )
              )}
            </select>

            {/* Price 1000+ Filter */}
            <label className="block mt-3 mb-2">Price 1000+:</label>
            <select
              className="w-full p-2 border rounded"
              onChange={(e) =>
                handleFilterChange("price_1000plus", e.target.value)
              }
            >
              <option value="">All</option>
              {[...new Set(products.map((p) => p.price_1000plus))].map(
                (price) => (
                  <option key={price} value={price}>
                    {getCurrencySymbol()} {convertPrice(price)}
                  </option>
                )
              )}
            </select>

            {/* Reset Filters Button */}
            <button
              className="w-full mt-4 bg-red-500 text-white py-2 rounded hover:bg-red-600"
              onClick={resetFilters}
            >
              Reset Filters
            </button>
          </div>

          {/* Main Table Section */}
          <div className="w-3/4 ml-5">
            <StickyTableHeader />
            <SingleCategory />

            <div className="mt-4">
              <div className="flex justify-between items-center mb-6 p-4 bg-white shadow-lg rounded-lg">
                {/* Search Bar */}
                <div className="flex flex-grow justify-center">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border border-gray-300 p-3 rounded-lg w-full max-w-md shadow-md focus:ring-2 focus:ring-blue-500 focus:outline-none transition ease-in-out duration-200"
                  />
                </div>

                {/* Currency Selector */}
                <div className="ml-4">
                  <select
                    value={currency}
                    onChange={handleCurrencyChange}
                    className="bg-gray-100 border border-gray-300 text-gray-700 p-3 rounded-lg shadow-md focus:ring-2 focus:ring-blue-500 focus:outline-none transition ease-in-out duration-200"
                  >
                    <option value="Dollar">Dollar ($)</option>
                    <option value="Rupee">Rupee (₹)</option>
                    <option value="Pound">Pound (£)</option>
                  </select>
                </div>

                {/* View Cart Button */}
                <div className="ml-4">
                  <Link
                    to="/basket"
                    className="inline-block bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 shadow-md transform transition-all duration-200 hover:scale-105"
                  >
                    View Cart
                  </Link>
                </div>
              </div>

              <div
                // className="overflow-x-auto rounded-lg ring-1 ring-slate-900/10 table-container"
                className="rounded-lg ring-1 ring-slate-900/10 table-container"
                style={{ height: 600 }}
              >
                <table className="w-full text-left">
                  <thead>
                    <tr>
                      <th className="px-6 py-3">Name</th>
                      <th className="px-6 py-3">Bore (B)</th>
                      <th className="px-6 py-3">Thick (t)</th>
                      <th className="px-6 py-3">Thickness Tolerance</th>
                      <th className="px-6 py-3">Free Diameter (D)</th>
                      <th className="px-6 py-3">Clearance on Bore (Cb)</th>
                      <th className="px-6 py-3">Beam (b)</th>
                      <th className="px-6 py-3">Price 1-249</th>
                      <th className="px-6 py-3">Price 250-999</th>
                      <th className="px-6 py-3">Price 1000+</th>
                      <th className="px-6 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchedProducts.map((product) => (
                      <tr key={product.id} className="even:bg-gray-100">
                        <td className="px-6 py-4">{product.name}</td>
                        <td className="px-6 py-4">{product.bore}</td>
                        <td className="px-6 py-4">{product.thick}</td>
                        <td className="px-6 py-4">
                          +{product.thickness_tolerance}
                        </td>
                        <td className="px-6 py-4">{product.free_diameter}</td>
                        <td className="px-6 py-4">
                          {product.clearance_onbore}
                        </td>
                        <td className="px-6 py-4">{product.beam}</td>

                        <td className="px-6 py-4">
                          {getCurrencySymbol()}{" "}
                          {convertPrice(product.price_1_249)}
                        </td>
                        <td className="px-6 py-4">
                          {getCurrencySymbol()}{" "}
                          {convertPrice(product.price_250_999)}
                        </td>
                        <td className="px-6 py-4">
                          {getCurrencySymbol()}{" "}
                          {convertPrice(product.price_1000plus)}
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-4">
                            {" "}
                            {/* Add flexbox with space between items */}
                            <QuantityControl
                              value={counters[product.id] || 1}
                              onChange={(e) => handleInputChange(e, product.id)}
                              increment={() => increment(product.id)}
                              decrement={() => decrement(product.id)}
                            />
                            <AddToCard
                              product={product}
                              quantity={counters[product.id] || 1}
                              openPopup={() =>
                                openPopup(product, counters[product.id] || 1)
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Table;
